import { H1 } from '@hub/heading'
import { Stack } from '@hub/stack'
import { LinkPagination } from '@scentregroup/shared/components/pagination'
import ProductsList from '@scentregroup/shared/components/products-list'
import { ProductListHeader } from '@scentregroup/shared/hub-components/product-list-header'
import { OnSearchFunc } from '@scentregroup/shared/hub-components/searchable-faceted-filter'
import { IMarketplaceProductsSearchResult } from '@scentregroup/shared/types'
import { Checkbox, Heading, Markdown, Spinner } from '@scentregroup/ui'
import classnames from 'classnames'
import React from 'react'
import {
  IBannerProps,
  ICategoryDataProps,
  IHeadingProps,
  IHeroImageSet,
  IProductsIndexState,
} from '.'
import PageMetaData from '../../../shared/component/page-meta-data'
import Breadcrumbs, { IBreadcrumb } from '../../components/breadcrumbs'
import EmptyProduct from '../../components/empty-product'
import FacetFilteredPills from '../../components/facet-filtered-pills'
import FacetedFilters from '../../components/faceted-filters'
import { CategoryList } from '../../components/faceted-filters/category-list'
import ItemsPerPageDropdown from '../../components/items-per-page-dropdown'
import { ResultCounter } from '../../components/result-counter'
import SortOptionsDropdown, {
  DropdownOption,
  SortOption,
} from '../../components/sort-options'
import { IFacet, IFacetSelections } from '../../types/facets'
import FooterLinks, { IFooterLinkProps } from './footer-links'
import CategoryLinks from './category-links'
import style from './index.module.scss'

interface IResultProps {
  start: number
  end: number
  numberOfProducts: number
}

interface IPaginationProps {
  numberOfPages: number
  onClickPaginationLink: (page: number) => void
  makePageLinkForPagination: (page: number) => string
}

interface IDropdownOptionsProps {
  onChangeItemsPerPage: (itemsPerPage: number) => void
  onClickItemsPerPage: () => void
}

interface ISortOptionsProps {
  onChangeSortOption: (sort: SortOption) => void
  onClickSortOption: () => void
  sortByOptions: DropdownOption[]
}

interface IFacetFilterProps {
  onChangeFacetSelections: (facetSelections: IFacetSelections) => void
  onChangeProductTypeSelections: (productTypes: string[]) => void
  onClearAll: () => void
  onChangeSort: (sort: SortOption) => void
  onChangeOnSale: (value: boolean) => void
  onFacetOptionSearch: OnSearchFunc
}

interface ILoadingProps {
  products: boolean | undefined
  category: boolean | undefined
}

export interface IBlurbProps {
  title: string
  body: string
  id?: string
}
interface IProductsSimplifiedProps {
  categoryData: ICategoryDataProps
  header: IHeadingProps
  breadcrumbs: IBreadcrumb[]
  isLoading: ILoadingProps
  result: IResultProps
  facets: IFacet[]
  productsIndexState: IProductsIndexState
  categoryTreeSearchParams: string
  basePath: string
  products: IMarketplaceProductsSearchResult[]
  productsListAnalytics: { productList: string }
  facetfilter: IFacetFilterProps
  sortOptions: ISortOptionsProps
  dropdownOptions: IDropdownOptionsProps
  pagination: IPaginationProps
  footerLinksLabel?: string
}

interface IBrandProductsSimplifiedProps extends IProductsSimplifiedProps {
  pageType: 'BRAND'
  banner?: never
  blurb?: never
  footerLinks?: never
}

interface ICollectionProductsSimplifiedProps
  extends Omit<IProductsSimplifiedProps, 'banner'> {
  pageType: 'COLLECTION'
  banner: Omit<IBannerProps, 'heroImageSet'> & {
    heroImageSet: IHeroImageSet
  }
  blurb?: IBlurbProps
  footerLinks?: IFooterLinkProps[]
}

interface IProductsProductsSimplifiedProps extends IProductsSimplifiedProps {
  pageType: 'PRODUCTS'
  banner?: never
  blurb?: IBlurbProps
  footerLinks?: IFooterLinkProps[]
}

interface ICannedSearchProductsSimplifiedProps
  extends IProductsSimplifiedProps {
  pageType: 'CANNED_SEARCH'
  banner?: never
  blurb?: IBlurbProps
  footerLinks?: IFooterLinkProps[]
}

interface IPromotionProductsSimplifiedProps
  extends Omit<IProductsSimplifiedProps, 'banner'> {
  pageType: 'PROMOTION'
  blurb: IBlurbProps
  banner: Omit<
    IBannerProps,
    'heroImageSet' | 'termsAndConditions' | 'shortDescription'
  > & {
    heroImageSet: IHeroImageSet | null
    termsAndConditions: string
    shortDescription: string | null
  }
  footerLinks?: never
}

type ProductsSimplifiedProps =
  | IBrandProductsSimplifiedProps
  | ICollectionProductsSimplifiedProps
  | IProductsProductsSimplifiedProps
  | IPromotionProductsSimplifiedProps
  | ICannedSearchProductsSimplifiedProps

const ProductsSimplified: React.FC<
  React.PropsWithChildren<ProductsSimplifiedProps>
> = ({
  pageType,
  categoryData,
  header,
  breadcrumbs,
  isLoading,
  result,
  facets,
  productsIndexState,
  facetfilter,
  categoryTreeSearchParams,
  basePath,
  sortOptions,
  products,
  productsListAnalytics,
  blurb,
  dropdownOptions,
  pagination,
  banner,
  footerLinks,
}) => {
  const { title, description, heading } = header
  const { start, end, numberOfProducts } = result
  const {
    onChangeFacetSelections,
    onChangeProductTypeSelections,
    onClearAll,
    onChangeSort,
    onChangeOnSale,
    onFacetOptionSearch,
  } = facetfilter
  const { onChangeSortOption, onClickSortOption, sortByOptions } = sortOptions
  const { onChangeItemsPerPage, onClickItemsPerPage } = dropdownOptions
  const { numberOfPages, onClickPaginationLink, makePageLinkForPagination } =
    pagination
  const onSaleFacet = facets.find(facet => facet.id === 'on-sale')
  const hasOnSaleProducts = onSaleFacet?.options.some(
    option => option.id === 'true'
  )
  const facetsWithoutOnSaleFilter = facets.filter(
    facet => facet.id !== 'on-sale'
  )
  const isPromotionCollectionPage = pageType === 'PROMOTION'

  const discriminatedProps = React.useMemo(() => {
    if (pageType === 'PROMOTION') {
      const layout = 'slim' as const
      const termsAndConditionsLink = blurb.id ? `#${blurb.id}` : undefined
      return { layout, banner, termsAndConditionsLink }
    } else if (pageType === 'COLLECTION') {
      return { layout: undefined, banner }
    }
    return null
  }, [pageType, blurb?.id, banner])
  return (
    <>
      {categoryData?.category && (
        <PageMetaData
          getMetaData={() => ({
            title,
            description,
          })}
        />
      )}
      {discriminatedProps && <ProductListHeader {...discriminatedProps} />}
      <div className={classnames(style.headingWrapper, {})}>
        <Stack
          gap="spacing-sm"
          sx={{ width: 'size-full' }}
          shouldWrapChildren={false}
        >
          {!isPromotionCollectionPage && <H1>{heading}</H1>}
          <Stack
            gap="spacing-sm"
            direction={['column', null, 'row']}
            shouldWrapChildren={false}
            flexGrow={0}
          >
            <Breadcrumbs breadcrumbs={breadcrumbs} />
            {!isLoading.products && (
              <ResultCounter
                start={start}
                end={end}
                totalProducts={numberOfProducts}
              />
            )}
          </Stack>
        </Stack>
      </div>
      <div className={style.bodyWrapper}>
        <div className={style.categoryLinksMobile}>
          <CategoryLinks
            categories={
              categoryData.category.content?.displaySiblingLinks
                ? categoryData.category.parent?.children
                : undefined
            }
            relatedLinks={categoryData.category.content?.relatedLinks}
          />
          <FacetFilteredPills
            facets={facets}
            facetSelections={productsIndexState.facetSelections}
            onChangeFacetSelections={onChangeFacetSelections}
            productTypes={categoryData?.category.productTypes}
            productTypeSelections={productsIndexState.productTypes}
            onChangeProductTypeSelections={onChangeProductTypeSelections}
          />
        </div>

        {!isLoading.category && (
          <div className={style.filterPanelSection}>
            <FacetedFilters
              category={categoryData?.category}
              facets={facetsWithoutOnSaleFilter}
              facetSelections={productsIndexState.facetSelections}
              onChangeFacetSelections={onChangeFacetSelections}
              productTypeSelections={productsIndexState.productTypes}
              onChangeProductTypeSelections={onChangeProductTypeSelections}
              onClearAll={onClearAll}
              sort={productsIndexState.sort}
              sortOptions={sortByOptions}
              onChangeSort={onChangeSort}
              onSale={productsIndexState.onSale}
              hasOnSaleProducts={hasOnSaleProducts}
              onChangeOnSale={onChangeOnSale}
              categoryListElement={
                categoryData.category && (
                  <CategoryList
                    category={categoryData.category}
                    search={categoryTreeSearchParams}
                    basePath={basePath}
                  />
                )
              }
              onFacetOptionSearch={onFacetOptionSearch}
              isLinked={true}
            />
          </div>
        )}
        <div className={style.productListSection}>
          <div className={style.products}>
            <div className={style.categoryLinksDesktop}>
              <CategoryLinks
                categories={
                  categoryData.category.content?.displaySiblingLinks
                    ? categoryData.category.parent?.children
                    : undefined
                }
                relatedLinks={categoryData.category.content?.relatedLinks}
                isMobile={false}
              />
              <FacetFilteredPills
                facets={facets}
                facetSelections={productsIndexState.facetSelections}
                onChangeFacetSelections={onChangeFacetSelections}
                productTypes={categoryData?.category.productTypes}
                productTypeSelections={productsIndexState.productTypes}
                onChangeProductTypeSelections={onChangeProductTypeSelections}
              />
            </div>

            <div className={style.sortWrapper}>
              <div className={style.sortDropdown}>
                <SortOptionsDropdown
                  option={productsIndexState.sort}
                  onChange={onChangeSortOption}
                  onClick={onClickSortOption}
                  options={sortByOptions}
                />
              </div>
              {!isPromotionCollectionPage && hasOnSaleProducts && (
                <div className={style.onSaleCheckboxWrapper}>
                  <Checkbox
                    checked={productsIndexState.onSale}
                    classNames={{
                      wrapper: style.onSaleCheckbox,
                      label: style.onSaleCheckboxLabel,
                      field: style.onSaleCheckboxField,
                    }}
                    label="Sale items"
                    onChange={onChangeOnSale}
                    size={'SMALL'}
                    id="sale-items"
                  />
                </div>
              )}
            </div>
            {isLoading.category || isLoading.products ? (
              <Spinner type="large" />
            ) : products.length == 0 ? (
              <EmptyProduct />
            ) : (
              <ProductsList
                products={products}
                page={productsIndexState.page}
                itemsPerPage={productsIndexState.itemsPerPage}
                analytics={productsListAnalytics}
                sort={productsIndexState.sort}
              />
            )}
          </div>
          <div
            className={classnames(style.bottomWrapper, {
              [style.bottomWrapperNoMargin]: !blurb && !footerLinks,
            })}
          >
            <div className={style.itemsPerPageDropdown}>
              <ItemsPerPageDropdown
                value={productsIndexState.itemsPerPage}
                onChange={onChangeItemsPerPage}
                onClick={onClickItemsPerPage}
              />
            </div>
            {!isLoading.products && (
              <LinkPagination
                currentPage={productsIndexState.page}
                totalPages={numberOfPages > 0 ? numberOfPages : 1}
                onLinkClick={onClickPaginationLink}
                makePageLink={makePageLinkForPagination}
              />
            )}
          </div>
          {blurb && (
            <div
              {...(blurb.id ? { id: blurb.id } : {})}
              className={classnames(style.contentWrapper, {
                [style.bottomWrapperNoMargin]: !footerLinks,
              })}
            >
              <Heading type="extrasmall" className={style.contentHeading}>
                {blurb.title}
              </Heading>
              <div className={style.markdownWrapper}>
                <Markdown content={blurb.body} />
              </div>
            </div>
          )}
          {footerLinks && footerLinks.length > 0 && (
            <div
              className={classnames(
                style.contentWrapper,
                style.contentWrapperNoMargin
              )}
            >
              <Heading type="extrasmall" className={style.contentHeading}>
                {pageType === 'CANNED_SEARCH'
                  ? 'Related links'
                  : 'Trending searches'}
              </Heading>
              <FooterLinks links={footerLinks} />
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default ProductsSimplified
